// AuraMax social API client. All calls are same-origin with the session cookie.
const API = process.env.REACT_APP_BACKEND_URL + "/api";

async function req(path, { method = "GET", body, form } = {}) {
  const opts = { method, credentials: "include", headers: {} };
  if (form) opts.body = form;
  else if (body !== undefined) { opts.headers["Content-Type"] = "application/json"; opts.body = JSON.stringify(body); }
  const res = await fetch(`${API}${path}`, opts);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(fmtDetail(data.detail));
  return data;
}

function fmtDetail(detail) {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ") || "Something went wrong";
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Something went wrong";
}

export const mediaUrl = (u) => (!u ? "" : u.startsWith("http") || u.startsWith("data:") || u.startsWith("blob:") ? u : process.env.REACT_APP_BACKEND_URL + u);

export const api = {
  // profile
  usernameAvailable: (u) => req(`/users/username-available?username=${encodeURIComponent(u)}`),
  getUser: (username) => req(`/users/${username}`),
  updateProfile: (body) => req(`/profile`, { method: "PUT", body }),
  setUsername: (username) => req(`/profile/username`, { method: "PUT", body: { username } }),
  // ui flags
  getFlags: () => req(`/me/flags`),
  setFlags: (body) => req(`/me/flags`, { method: "PUT", body }),
  // media
  uploadMedia: (file) => { const f = new FormData(); f.append("file", file); return req(`/media/upload`, { method: "POST", form: f }); },
  processVideo: (body) => req(`/media/process-video`, { method: "POST", body }),
  transcribe: (file) => { const f = new FormData(); f.append("file", file); return req(`/transcribe`, { method: "POST", form: f }); },
  setTyping: (id) => req(`/conversations/${id}/typing`, { method: "POST" }),
  getTyping: (id) => req(`/conversations/${id}/typing`),
  // posts
  createPost: (body) => req(`/posts`, { method: "POST", body }),
  getPost: (id) => req(`/posts/${id}`),
  deletePost: (id) => req(`/posts/${id}`, { method: "DELETE" }),
  userPosts: (username, type) => req(`/users/${username}/posts${type ? `?type=${type}` : ""}`),
  feed: (kind, skip = 0, limit = 15) => req(`/feed/${kind}?skip=${skip}&limit=${limit}`),
  // interactions
  like: (id) => req(`/posts/${id}/like`, { method: "POST" }),
  unlike: (id) => req(`/posts/${id}/like`, { method: "DELETE" }),
  repost: (id) => req(`/posts/${id}/repost`, { method: "POST" }),
  unrepost: (id) => req(`/posts/${id}/repost`, { method: "DELETE" }),
  comments: (id) => req(`/posts/${id}/comments`),
  addComment: (id, text, parent_id) => req(`/posts/${id}/comments`, { method: "POST", body: { text, parent_id } }),
  deleteComment: (id) => req(`/comments/${id}`, { method: "DELETE" }),
  likeComment: (id) => req(`/comments/${id}/like`, { method: "POST" }),
  unlikeComment: (id) => req(`/comments/${id}/like`, { method: "DELETE" }),
  // follows
  follow: (username) => req(`/users/${username}/follow`, { method: "POST" }),
  unfollow: (username) => req(`/users/${username}/follow`, { method: "DELETE" }),
  followers: (username) => req(`/users/${username}/followers`),
  following: (username) => req(`/users/${username}/following`),
  friends: () => req(`/friends`),
  // discovery
  searchUsers: (q, category) => req(`/search/users?q=${encodeURIComponent(q || "")}${category ? `&category=${encodeURIComponent(category)}` : ""}`),
  discover: (category) => (category ? req(`/search/users?q=&category=${encodeURIComponent(category)}`) : req(`/discovery/creators`)),
  allUsers: (q = "", skip = 0, limit = 24) => req(`/users?q=${encodeURIComponent(q)}&skip=${skip}&limit=${limit}`),
  // team
  team: () => req(`/team`),
  teamAutoFollow: () => req(`/team/auto-follow`, { method: "POST" }),
  // notifications
  notifications: () => req(`/notifications`),
  notificationsUnread: () => req(`/notifications/unread-count`),
  notificationsReadAll: () => req(`/notifications/read-all`, { method: "POST" }),
  // stories
  createStory: (media, caption) => req(`/stories`, { method: "POST", body: { media, caption } }),
  sharePostToStory: (post_id, caption) => req(`/stories`, { method: "POST", body: { post_id, caption } }),
  deleteStory: (id) => req(`/stories/${id}`, { method: "DELETE" }),
  storiesTray: () => req(`/stories/tray`),
  userStories: (username) => req(`/users/${username}/stories`),
  viewStory: (id) => req(`/stories/${id}/view`, { method: "POST" }),
  storyViewers: (id) => req(`/stories/${id}/viewers`),
  // moderation
  report: (username, reason) => req(`/users/${username}/report`, { method: "POST", body: { reason } }),
  block: (username) => req(`/users/${username}/block`, { method: "POST" }),
  mute: (username) => req(`/users/${username}/mute`, { method: "POST" }),
  // dms
  conversations: () => req(`/conversations`),
  startConversation: (username) => req(`/conversations`, { method: "POST", body: { username } }),
  messages: (id, before) => req(`/conversations/${id}/messages${before ? `?before=${encodeURIComponent(before)}` : ""}`),
  sendMessage: (id, text, media, reply_to, client_id) => req(`/conversations/${id}/messages`, { method: "POST", body: { text, media, reply_to, client_id } }),
  markSeen: (id) => req(`/conversations/${id}/seen`, { method: "POST" }),
  reactMessage: (id, mid, emoji) => req(`/conversations/${id}/messages/${mid}/reactions`, { method: "POST", body: { emoji } }),
  deleteMessage: (id, mid, scope) => req(`/conversations/${id}/messages/${mid}?scope=${scope}`, { method: "DELETE" }),
  searchConversation: (id, q) => req(`/conversations/${id}/search?q=${encodeURIComponent(q)}`),
  leaveConversation: (id) => req(`/conversations/${id}`, { method: "DELETE" }),
  presence: (userId) => req(`/presence?user_id=${encodeURIComponent(userId)}`),
  setRetention: (id, retention) => req(`/conversations/${id}/retention`, { method: "PUT", body: { retention } }),
  // saves
  savePost: (id) => req(`/posts/${id}/save`, { method: "POST" }),
  unsavePost: (id) => req(`/posts/${id}/save`, { method: "DELETE" }),
  mySaved: () => req(`/me/saved`),
  // aura
  auraUser: (username) => req(`/aura/user/${username}`),
  auraBreakdown: () => req(`/aura/me/breakdown`),
  auraLeaderboard: () => req(`/aura/leaderboard`),
  // growth: onboarding / what's new / completion
  onboardingComplete: () => req(`/me/onboarding/complete`, { method: "POST" }),
  onboardingSuggestions: () => req(`/onboarding/suggestions`),
  followAll: (usernames) => req(`/onboarding/follow-all`, { method: "POST", body: { usernames } }),
  whatsNewSeen: () => req(`/me/whats-new/seen`, { method: "POST" }),
  completion: () => req(`/me/completion`),
  completionSeen: () => req(`/me/completion/seen`, { method: "POST" }),
  // public profile
  publicProfile: (username) => req(`/public/${username}`),
  // for you seen tracking
  feedSeen: (post_ids) => req(`/feed/seen`, { method: "POST", body: { post_ids } }),
  // referrals / market
  trackReferral: (code) => req(`/referrals/track?code=${encodeURIComponent(code)}`, { method: "POST" }),
  pendingReferral: () => req(`/referrals/pending`),
  attachReferral: (code) => req(`/referrals/attach`, { method: "POST", body: code ? { code } : {} }),
  myReferrals: () => req(`/referrals/me`),
  market: () => req(`/market`),
  // reports / blocks
  reportReasons: () => req(`/reports/reasons`),
  createReport: (target_type, target_id, reason, details) => req(`/reports`, { method: "POST", body: { target_type, target_id, reason, details } }),
  unblock: (username) => req(`/users/${username}/block`, { method: "DELETE" }),
  blockedAccounts: () => req(`/me/blocked`),
  // admin
  adminFeatured: () => req(`/admin/featured`),
  adminSetFeatured: (usernames) => req(`/admin/featured`, { method: "PUT", body: { usernames } }),
  adminReports: (status = "open") => req(`/admin/reports?status=${status}`),
  adminResolve: (id, action) => req(`/admin/reports/${id}/resolve`, { method: "POST", body: { action } }),
  adminReferrals: (q) => req(`/admin/referrals?q=${encodeURIComponent(q)}`),
  adminReferralFraud: (id, fraudulent) => req(`/admin/referrals/${id}/fraud`, { method: "POST", body: { fraudulent } }),
  // email notifications
  emailPrefs: () => req(`/me/email-prefs`),
  setEmailPrefs: (prefs) => req(`/me/email-prefs`, { method: "PUT", body: prefs }),
  setTimezone: (timezone) => req(`/me/timezone`, { method: "POST", body: { timezone } }),
  unsubscribeInfo: (token) => req(`/email/unsubscribe?token=${encodeURIComponent(token)}`),
  unsubscribe: (token, all = false) => req(`/email/unsubscribe?token=${encodeURIComponent(token)}&all=${all}`, { method: "POST" }),
  adminEmailStats: () => req(`/admin/emails/stats`),
  adminEmailPause: (paused) => req(`/admin/emails/pause`, { method: "PUT", body: { paused } }),
  adminEmailTest: () => req(`/admin/emails/test`, { method: "POST" }),
  adminEmailRun: () => req(`/admin/emails/run-now`, { method: "POST" }),
  // solana wallet (read-only, non-custodial)
  walletMe: () => req(`/wallet/me`),
  walletBalances: (refresh = false) => req(`/wallet/balances${refresh ? "?refresh=true" : ""}`),
  walletChallenge: (address) => req(`/wallet/challenge`, { method: "POST", body: { address } }),
  walletVerify: (address, message, signature, wallet_name) => req(`/wallet/verify`, { method: "POST", body: { address, message, signature, wallet_name } }),
  walletUnlink: () => req(`/wallet/me`, { method: "DELETE" }),
  adminWalletStatus: () => req(`/admin/wallet/status`),
  // $AURAMAX Genesis (read-only provider data; purchase link is admin-gated)
  genesis: () => req(`/genesis`),
  adminGenesis: () => req(`/admin/genesis`),
  adminGenesisUpdate: (body) => req(`/admin/genesis`, { method: "PUT", body }),
  adminGenesisRefresh: () => req(`/admin/genesis/refresh`, { method: "POST" }),
  // Ambassadors / analytics / owner command center
  genesisTrack: (body) => req(`/genesis/track`, { method: "POST", body }),
  genesisAmbassadors: () => req(`/genesis/ambassadors`),
  adminGenesisEngagement: () => req(`/admin/genesis/engagement`),
  announcement: () => req(`/announcement`),
  adminAnnouncements: () => req(`/admin/announcements`),
  adminAnnounce: (body) => req(`/admin/announcements`, { method: "POST", body }),
  adminRetireAnnouncement: (id) => req(`/admin/announcements/${id}`, { method: "DELETE" }),
  // AuraMax LIVE (Founders Channel)
  liveFounders: () => req(`/live/founders`),
  liveStatus: () => req(`/live/status`),
  liveEvents: () => req(`/live/events`),
  liveEvent: (id) => req(`/live/events/${id}`),
  liveRemind: (id) => req(`/live/events/${id}/remind`, { method: "POST" }),
  liveStart: (body) => req(`/live/broadcasts/start`, { method: "POST", body }),
  liveBroadcast: (id) => req(`/live/broadcasts/${id}`),
  liveJoin: (id) => req(`/live/broadcasts/${id}/join`, { method: "POST" }),
  liveLayout: (id, body) => req(`/live/broadcasts/${id}/layout`, { method: "POST", body }),
  liveMute: (id, identity, track_sid) => req(`/live/broadcasts/${id}/mute`, { method: "POST", body: { identity, track_sid } }),
  liveEnd: (id) => req(`/live/broadcasts/${id}/end`, { method: "POST" }),
  livePresence: (id) => req(`/live/broadcasts/${id}/presence`, { method: "POST" }),
  liveHeart: (id) => req(`/live/broadcasts/${id}/heart`, { method: "POST" }),
  liveChat: (id, after) => req(`/live/broadcasts/${id}/chat${after ? `?after=${encodeURIComponent(after)}` : ""}`),
  liveChatSend: (id, text) => req(`/live/broadcasts/${id}/chat`, { method: "POST", body: { text } }),
  liveChatSettings: (id, body) => req(`/live/broadcasts/${id}/chat/settings`, { method: "POST", body }),
  liveChatClear: (id) => req(`/live/broadcasts/${id}/chat/clear`, { method: "POST" }),
  adminLiveOverview: () => req(`/admin/live/overview`),
  adminLiveEmergency: (id) => req(`/admin/live/broadcasts/${id}/emergency`, { method: "POST" }),
  adminLiveCreateEvent: (body) => req(`/admin/live/events`, { method: "POST", body }),
  adminLiveUpdateEvent: (id, body) => req(`/admin/live/events/${id}`, { method: "PUT", body }),
  adminLiveDeleteEvent: (id) => req(`/admin/live/events/${id}`, { method: "DELETE" }),
  adminMembers: (q) => req(`/admin/members?q=${encodeURIComponent(q || "")}`),
  adminMember: (u) => req(`/admin/members/${u}`),
  adminMemberAction: (u, body) => req(`/admin/members/${u}/action`, { method: "POST", body }),
  adminDeletePost: (id) => req(`/admin/posts/${id}`, { method: "DELETE" }),
  adminAudit: () => req(`/admin/audit`),
};

export const UPDATE_VERSION = "2026-10-launch-1";
export const profileUrl = (username) => `/@${username}`;
// Preview-capable share link: only /api/* reaches the backend on this host, so the OG route lives there
// and 302s humans to /@username. (Clean /@username links work for people but can't render previews.)
export const shareUrl = (username) => `${process.env.REACT_APP_BACKEND_URL}/api/p/${username}`;
export const genesisShareUrl = (via) => `${process.env.REACT_APP_BACKEND_URL}/api/g${via ? `?via=${encodeURIComponent(via)}` : ""}`;
export function copyText(text) {
  return navigator.clipboard?.writeText(text).then(() => true).catch(() => legacyCopy(text)) ?? Promise.resolve(legacyCopy(text));
}
function legacyCopy(text) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select(); const ok = document.execCommand("copy"); document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}
export async function shareLink(url, title) {
  try { if (navigator.share) { await navigator.share({ url, title }); return "shared"; } } catch (e) { if (e?.name === "AbortError") return "cancel"; }
  return (await copyText(url)) ? "copied" : "failed";
}

// Compact Aura formatting (max ~3 significant digits, trims trailing .0).
export function auraFmt(centi) {
  const n = (centi || 0) / 100;
  const a = Math.abs(n);
  const f = (v, s) => { let x = v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2); x = x.replace(/\.?0+$/, ""); return x + s; };
  if (a >= 1e9) return f(n / 1e9, "B");
  if (a >= 1e6) return f(n / 1e6, "M");
  if (a >= 1e3) return f(n / 1e3, "K");
  return String(Math.round(n));
}
export const auraExact = (centi) => ((centi || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 });
export const multX = (bps) => ((bps || 10000) / 10000).toFixed(2) + "x";

// Deterministic per-person Aura-spectrum color (consistent everywhere for a given user).
const AURA_PALETTE = ["#ff2e93", "#ff7a3c", "#ffd23c", "#37e6b0", "#38e1ff", "#5b8cff", "#b06cff"];
export function userColor(seed) {
  const s = String(seed || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AURA_PALETTE[h % AURA_PALETTE.length];
}

// Relationship state from a public profile shape.
export function relationshipState(p) {
  if (!p || p.is_self) return "self";
  if (p.is_following && p.follows_me) return "friends";
  if (p.is_following) return "following";
  if (p.follows_me) return "follow_back";
  return "follow";
}

export function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 5) return "now";
  if (s < 60) return s + "s";
  if (s < 3600) return Math.floor(s / 60) + "m";
  if (s < 86400) return Math.floor(s / 3600) + "h";
  if (s < 604800) return Math.floor(s / 86400) + "d";
  if (s < 2419200) return Math.floor(s / 604800) + "w";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function fmt(n) {
  n = n || 0;
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
  return String(n);
}
